import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useAuthContext } from "../../auth/state/AuthContext";
import AdminEmailMailboxList from "../components/email/AdminEmailMailboxList";
import AdminEmailReader from "../components/email/AdminEmailReader";
import { adminEmailMailboxRefreshAtom, adminEmailMailboxStatusAtom } from "../state/admin-email.atoms";
import { adminStyles } from "../styles/admin.styles";
import { ADMIN_EMAIL_ACTION_CLASS } from "../utils/admin-email.constants";
import { formatAdminEmailDate, getAdminEmailInitialMailbox } from "../utils/admin-email.utils";

// Compose a read-only email workspace for the committed admin mailbox catalog.
// The sidebar owns count polling; this page shares those results and keeps message content local to its reader.
const AdminEmailPage = () => {
  const mailboxStatus = useAtomValue(adminEmailMailboxStatusAtom);
  const refreshCounts = useSetAtom(adminEmailMailboxRefreshAtom);
  const accountId = useAuthContext().state.user?.accessToken?.sub ?? "";
  const status = mailboxStatus.accountId === accountId ? mailboxStatus : { response: null, loading: true, error: null };
  const [selectedMailboxId, setSelectedMailboxId] = useState("");
  const [readerRevision, setReaderRevision] = useState(0);
  const mailboxes = status.response?.mailboxes ?? [];
  const mailbox = mailboxes.find((entry) => entry.id === selectedMailboxId) ?? getAdminEmailInitialMailbox(mailboxes);
  const hasUnavailableCounts = mailboxes.some((entry) => entry.error || entry.unreadCount === null);

  // Remember the initial choice so count polling cannot switch inboxes while someone reads a message.
  // Explicit selections remain available even when that inbox's count fails on a later refresh.
  useEffect(() => {
    if (mailbox && mailbox.id !== selectedMailboxId) {
      setSelectedMailboxId(mailbox.id);
    }
  }, [mailbox, selectedMailboxId]);

  const refresh = () => {
    refreshCounts((current) => current + 1);
    setReaderRevision((current) => current + 1);
  };

  return (
    <main
      data-test="admin-email-page"
      className={twMerge(adminStyles.page.pagePadding, "h-full min-w-0 flex-1 space-y-6 overflow-y-auto")}
    >
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className={adminStyles.heading.pageTitle}>Email</h1>
          <p className="mt-2 text-sm opacity-60">Read incoming requests from the OpsLink inboxes.</p>
          <p className="mt-1 text-xs opacity-60">Opening an email keeps its unread status unchanged.</p>
        </div>
        <button
          type="button"
          data-test="admin-email-refresh"
          onClick={refresh}
          disabled={status.loading}
          className={ADMIN_EMAIL_ACTION_CLASS}
        >
          <ArrowPathIcon className={twMerge("h-4 w-4", status.loading && "animate-spin")} />
          Refresh
        </button>
      </header>
      {status.error && (
        <p
          role="alert"
          data-test="admin-email-mailboxes-error"
          className="rounded bg-[#FFEAED] p-3 text-sm text-[#BF0002]"
        >
          {status.error}
        </p>
      )}
      {hasUnavailableCounts && !status.error && (
        <p
          role="status"
          data-test="admin-email-partial-availability"
          className="rounded bg-[#FFF8E1] p-3 text-sm text-[#9A6700]"
        >
          Some inbox counts are unavailable. You can still select another inbox to read its emails, or refresh to retry.
        </p>
      )}
      {!status.response && status.loading && (
        <p role="status" className="text-sm opacity-60">
          Loading inboxes…
        </p>
      )}
      {status.response && mailboxes.length === 0 && (
        <p data-test="admin-email-no-mailboxes" className="text-sm opacity-60">
          No inboxes are configured for this page.
        </p>
      )}
      {mailbox && (
        <div className="grid min-w-0 gap-5 xl:grid-cols-[230px_minmax(0,1fr)]">
          <aside className="min-w-0 space-y-3">
            <AdminEmailMailboxList
              mailboxes={mailboxes}
              selectedId={mailbox.id}
              countsUnavailable={Boolean(status.error)}
              onSelect={setSelectedMailboxId}
            />
            {status.response && (
              <p className="text-[11px] leading-5 opacity-50">
                Unread counts checked {formatAdminEmailDate(status.response.asOf)}. Counts refresh every minute.
              </p>
            )}
          </aside>
          <AdminEmailReader key={`${accountId}:${mailbox.id}:${readerRevision}`} mailbox={mailbox} />
        </div>
      )}
    </main>
  );
};

export default AdminEmailPage;
